import { acceptHMRUpdate, defineStore } from 'pinia'

import service from '@/services/logs.service'

type SortOrder = 'asc' | 'desc'

export type LogsSort = { key: string; order: SortOrder }
export type LogsDialog = {
  isOpen: boolean
  document: any
}

export interface LogsStoreState {
  logsSort: LogsSort[]
  logsFilters: Array<Filter>
  logsFiltersPaneIsOpen: boolean
  roomLogsSearchQuery: string
  logsDialog: LogsDialog
  fileLogsPending: boolean
  folderLogsPending: boolean
  roomLogsPending: boolean
  roomLogs: any
  logsTypesPending: boolean
  logsTypes: any
}

export interface LogsParams {
  search?: string
  beginDate?: string
  endDate?: string
  userIds?: string[]
  groupIds?: string[]
  types?: string[]
  sortBy?: string
  sortDesc?: boolean
}

export const useLogsStore = defineStore('logs', {
  state: (): LogsStoreState => ({
    logsSort: [{ key: 'date', order: 'desc' }],
    logsFilters: [],
    logsFiltersPaneIsOpen: false,
    roomLogsSearchQuery: '',
    logsDialog: {
      isOpen: false,
      document: null,
    },
    fileLogsPending: false,
    folderLogsPending: false,
    roomLogsPending: false,
    roomLogs: null,
    logsTypesPending: false,
    logsTypes: null,
  }),

  getters: {
    hasActiveFilters: (state): boolean => state.logsFilters.length > 0,

    getLogsParams: (state: LogsStoreState): LogsParams => {
      const params: LogsParams = {}

      if (state.roomLogsSearchQuery) params.search = state.roomLogsSearchQuery

      const dateFilter = state.logsFilters.find(f => f.category.key === 'date')
      if (dateFilter) {
        const [beginDate, endDate] = dateFilter.value.key as string[]
        if (beginDate) params.beginDate = beginDate
        if (endDate) params.endDate = endDate
      }

      const userFilters = state.logsFilters.filter(f => f.category.key === 'users')
      if (userFilters.length) params.userIds = userFilters.map(f => f.value.key) as string[]

      const groupFilters = state.logsFilters.filter(f => f.category.key === 'groups')
      if (groupFilters.length) params.groupIds = groupFilters.map(f => f.value.key) as string[]

      const typesFilters = state.logsFilters.filter(f => f.category.key === 'types')
      if (typesFilters.length) params.types = typesFilters.map(f => f.value.key) as string[]

      if (state.logsSort[0]?.key) {
        params.sortBy = state.logsSort[0].key
        params.sortDesc = state.logsSort[0].order === 'desc'
      }

      return params
    },
  },

  actions: {
    closeLogsDialog() {
      this.logsDialog = { isOpen: false, document: null }
    },
    openLogsDialog(document: any) {
      this.logsDialog = { isOpen: true, document }
    },
    removeLogFilter(filterToRemove: Filter) {
      this.logsFilters = this.logsFilters.filter(
        f =>
          f.category.key !== filterToRemove.category.key ||
          f.value.key !== filterToRemove.value.key,
      )
    },
    resetLogsSort() {
      this.logsSort = [{ key: 'date', order: 'desc' }]
    },

    async getLogsTypes(mnemo: string) {
      this.logsTypesPending = true
      try {
        const response = await service.getLogsTypes(mnemo)
        this.logsTypes = response.data.types
      } catch (error) {
        console.error('Error fetching logs types:', error)
        throw error
      } finally {
        this.logsTypesPending = false
      }
    },

    async getRoomLogs({ mnemo, params }: { mnemo: string; params: LogsParams }) {
      this.roomLogsPending = true
      try {
        const response = await service.getRoomLogs(mnemo, { params })
        this.roomLogs = response.data
      } catch (error) {
        console.error('Error fetching room logs:', error)
        throw error
      } finally {
        this.roomLogsPending = false
      }
    },

    async getFolderLogs({ mnemo, folderId }: { mnemo: string; folderId: number }) {
      this.folderLogsPending = true
      try {
        return await service.getFolderLogs(mnemo, folderId)
      } catch (error) {
        console.error('Error fetching folder logs:', error)
        throw error
      } finally {
        this.folderLogsPending = false
      }
    },

    async getFileLogs({ mnemo, fileId }: { mnemo: string; fileId: number }) {
      this.fileLogsPending = true
      try {
        return await service.getFileLogs(mnemo, fileId)
      } catch (error) {
        console.error('Error fetching file logs:', error)
        throw error
      } finally {
        this.fileLogsPending = false
      }
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLogsStore, import.meta.hot))
}
